import { LucideIcon } from "lucide-react";
import React from "react";

import InfoCard from "@/components/layout/InfoCard";
import SkeletonInfoCard from "@/components/layout/SkeletonInfoCard";
import { CardColorScheme } from "@/lib/constants/colors";
import { useNextEventByTag } from "@/lib/hooks/useNextEventByTag";
import { calendarEventToEvent } from "@/lib/utils/eventTransformers";
import { Event, EventType } from "@/types/events";

interface NextEventCardProps {
  // Query parameters
  groupId: string;
  tags: string[];

  // Display configuration
  title: string;
  subtitle: string;
  eventType: EventType;
  colorScheme: CardColorScheme;
  icon: LucideIcon;

  // Fallback configuration (when no event found)
  fallbackTitle: string;
  fallbackDescription: string;
  fallbackLocation: string;
  fallbackAudience: string;

  // Optional link
  detailsUrl?: string;
  detailsLinkText?: string;
}

/**
 * NextEventCard - Displays the next upcoming event for a specific group and tag combination
 *
 * This component handles:
 * - Loading state with skeleton
 * - Error/no event state with fallback content
 * - Success state with event details
 *
 * @example
 * <NextEventCard
 *   groupId="400G"
 *   tags={["ceremony", "court-of-honor"]}
 *   title="Next Court of Honor"
 *   subtitle="Honoring the hard work of our Scouts"
 *   eventType="ceremony"
 *   colorScheme="scout-matrix-yellow"
 *   icon={Award}
 *   fallbackTitle="No upcoming Court of Honor"
 *   fallbackDescription="Check our calendar for the latest ceremony schedule."
 *   fallbackLocation="Location TBD"
 *   fallbackAudience="Scouts and Families"
 * />
 */
export function NextEventCard({
  groupId,
  tags,
  title,
  subtitle,
  eventType,
  colorScheme,
  icon,
  fallbackTitle,
  fallbackDescription,
  fallbackLocation,
  fallbackAudience,
  detailsUrl,
  detailsLinkText,
}: NextEventCardProps) {
  const { event, loading, error } = useNextEventByTag({ groupId, tags });

  if (loading) {
    return (
      <SkeletonInfoCard
        title={title}
        subtitle="Loading from calendar..."
        colorScheme={colorScheme}
        icon={icon}
      />
    );
  }

  if (!event || error) {
    const fallbackEvent: Event = {
      id: `no-${tags[0] ?? "event"}`,
      title: fallbackTitle,
      description: fallbackDescription,
      startDate: new Date(2025, 0, 1), // Valid date for parsing
      location: fallbackLocation,
      audience: fallbackAudience,
      type: eventType,
    };
    return (
      <InfoCard
        title={title}
        subtitle={subtitle}
        eventDetails={fallbackEvent}
        colorScheme={colorScheme}
        icon={icon}
      />
    );
  }

  const transformedEvent = calendarEventToEvent(event, eventType);

  return (
    <InfoCard
      title={title}
      subtitle={subtitle}
      eventDetails={transformedEvent}
      colorScheme={colorScheme}
      icon={icon}
      detailsUrl={detailsUrl}
      detailsLinkText={detailsLinkText}
    />
  );
}
